import {StyleSheet} from "aphrodite";
import React, {useCallback, useMemo} from "react";

import {
    MultiSelect,
    OptionItem,
    type LabelsValues,
} from "@khanacademy/wonder-blocks-dropdown";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

import {getLabels, strings} from "./strings.ts";
import type {
    Course,
    CourseDropdownProps,
    GradeLevel,
    GradeLevelDropdownProps,
    TeacherCourse,
    TeacherGradeLevel,
} from "./types.ts";

const defaultGradeLevels: GradeLevel[] = [
    {id: "FIRST", displayName: () => strings.firstGrade()},
    {id: "SECOND", displayName: () => strings.secondGrade()},
    {id: "THIRD", displayName: () => strings.thirdGrade()},
    {id: "FOURTH", displayName: () => strings.fourthGrade()},
    {id: "FIFTH", displayName: () => strings.fifthGrade()},
    {id: "SIXTH", displayName: () => strings.sixthGrade()},
    {id: "SEVENTH", displayName: () => strings.seventhGrade()},
    {id: "EIGHTH", displayName: () => strings.eighthGrade()},
    {id: "NINTH", displayName: () => strings.ninthGrade()},
    {id: "TENTH", displayName: () => strings.tenthGrade()},
    {id: "ELEVENTH", displayName: () => strings.eleventhGrade()},
    {id: "TWELFTH", displayName: () => strings.twelfthGrade()},
    {id: "COLLEGE", displayName: () => strings.collegeAndBeyond()},
];

export const GradeLevelsDropdown: React.FC<GradeLevelDropdownProps> = ({
    selectedValues,
    onChange,
}) => {
    const gradeLevelListOptions = useMemo(
        () =>
            defaultGradeLevels.map((gradeLevel) => (
                <OptionItem
                    label={gradeLevel.displayName()}
                    value={gradeLevel.id}
                    key={gradeLevel.id}
                    testId="assignment-class-option"
                />
            )),
        [],
    );

    const labels: LabelsValues = getLabels(
        strings.gradeSelectionPlaceHolder(),
        (numSelectedValues: number) =>
            strings.numGradeLevels(numSelectedValues),
        strings.allSubjectsSelected(),
        (numOptions: number) => strings.selectAllGradeLevelLabel(numOptions),
    );

    const handleChange = useCallback(
        (newSelectedValues: string[]) => {
            onChange(newSelectedValues as TeacherGradeLevel[]);
        },
        [onChange],
    );

    return (
        <>
            <LabelMedium>{strings.gradesYouTeach()}</LabelMedium>
            <Strut size={spacing.xSmall_8} />
            <MultiSelect
                labels={labels}
                selectedValues={selectedValues as string[]}
                onChange={handleChange}
                testId="grade-levels-dropdown"
                style={styles.dropdownWidth}
            >
                {gradeLevelListOptions}
            </MultiSelect>
        </>
    );
};

const defaultCourses: Course[] = [
    {id: "MATH", displayName: () => strings.math()},
    {id: "ENGLISH", displayName: () => strings.english()},
    {id: "SCIENCE", displayName: () => strings.science()},
    {id: "HISTORY", displayName: () => strings.history()},
    {id: "SOCIAL_STUDIES", displayName: () => strings.socialStudies()},
    {id: "ECONOMICS", displayName: () => strings.economics()},
    {id: "WORLD_LANGUAGES", displayName: () => strings.worldLanguages()},
    {id: "COMPUTING", displayName: () => strings.computing()},
    {id: "ARTS_AND_HUMANITIES", displayName: () => strings.artsAndHumanities()},
    {id: "LIFE_SKILLS", displayName: () => strings.lifeSkills()},
];

export const CourseDropdown: React.FC<CourseDropdownProps> = ({
    selectedValues,
    onChange,
}) => {
    const courseListOptions = useMemo(
        () =>
            defaultCourses.map((course) => (
                <OptionItem
                    label={course.displayName()}
                    value={course.id}
                    key={course.id}
                    testId="assignment-course-option"
                />
            )),
        [],
    );

    const labels: LabelsValues = getLabels(
        strings.subjectSelectionPlaceholder(),
        (numSelectedValues: number) => strings.numSubjects(numSelectedValues),
        strings.allSubjectsSelected(),
        (numOptions: number) => strings.selectAllSubjectLabel(numOptions),
    );

    const handleChange = useCallback(
        (newSelectedValues: Array<string>) => {
            onChange(newSelectedValues as TeacherCourse[]);
        },
        [onChange],
    );

    return (
        <>
            <LabelMedium>{strings.subjectsYouTeach()}</LabelMedium>
            <Strut size={spacing.xSmall_8} />
            <MultiSelect
                labels={labels}
                selectedValues={selectedValues as string[]}
                onChange={handleChange}
                testId="course-dropdown"
                style={styles.dropdownWidth}
            >
                {courseListOptions}
            </MultiSelect>
        </>
    );
};

const styles = StyleSheet.create({
    dropdownWidth: {
        width: "100%",
    },
});
